import { ChevronLeft, ChevronRight } from "lucide-react";
import { parseIsoDateOnlyLocal } from "@/lib/dateUtils";

const WEEKDAY_LABELS = ["D", "S", "T", "Q", "Q", "S", "S"] as const;

function toIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function monthStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date: Date, delta: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + delta, 1);
}

function buildMonthGrid(viewMonth: Date): (Date | null)[] {
  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push(new Date(year, month, day));
  }
  return cells;
}

function formatMonthYear(viewMonth: Date): string {
  const label = viewMonth.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  return label.charAt(0).toLowerCase() + label.slice(1);
}

export type PeriodRangeCalendarProps = {
  viewMonth: Date;
  onViewMonthChange: (month: Date) => void;
  selectedIso: string;
  onSelect: (iso: string) => void;
  primaryColor: string;
  ariaLabel: string;
};

export function PeriodRangeCalendar({
  viewMonth,
  onViewMonthChange,
  selectedIso,
  onSelect,
  primaryColor,
  ariaLabel,
}: PeriodRangeCalendarProps) {
  const cells = buildMonthGrid(viewMonth);

  return (
    <div
      className="rounded-xl border border-dashed border-gray-300 bg-white p-4"
      role="group"
      aria-label={ariaLabel}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <button
          type="button"
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
          aria-label="Mês anterior"
          onClick={() => onViewMonthChange(addMonths(viewMonth, -1))}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="min-w-0 truncate text-sm font-medium capitalize text-gray-900">
          {formatMonthYear(viewMonth)}
        </span>
        <button
          type="button"
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
          aria-label="Próximo mês"
          onClick={() => onViewMonthChange(addMonths(viewMonth, 1))}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-y-1 text-center">
        {WEEKDAY_LABELS.map((label, idx) => (
          <span key={`${label}-${idx}`} className="py-1 text-xs font-medium text-muted-foreground">
            {label}
          </span>
        ))}
        {cells.map((date, idx) => {
          if (!date) {
            return <span key={`empty-${idx}`} aria-hidden />;
          }
          const iso = toIsoDate(date);
          const selected = selectedIso === iso;
          return (
            <button
              key={iso}
              type="button"
              aria-label={date.toLocaleDateString("pt-BR")}
              aria-pressed={selected}
              onClick={() => onSelect(iso)}
              className={`mx-auto flex h-8 w-8 items-center justify-center rounded-full text-sm transition-colors ${
                selected ? "font-medium text-white" : "text-gray-900 hover:bg-gray-100"
              }`}
              style={selected ? { backgroundColor: primaryColor } : undefined}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function isoFromState(value: string): Date {
  if (value.length === 10) return monthStart(parseIsoDateOnlyLocal(value));
  return monthStart(new Date());
}
